// Revenue-CTA targets: what the site points readers at, and how it picks.
//
// There are two halves, and the split is the whole point:
//
//  1. WHAT'S ON — the MavenOffering table (admin.gazar.dev → Maven), fetched by
//     the route loaders and passed to `selectLiveOffers()`. Sessions expire on
//     their own: a Lightning Lesson stops being promoted once it has happened,
//     so nobody has to remember to take it down. Add the next one in the admin,
//     with a date, and every page picks it up.
//  2. HOW WE ROUTE — the rules in this file: which offer suits which article
//     category, and when to also surface 1:1 mentorship. That's logic, not
//     content, so it stays here where it's typed and tested.
//
// The constants below are the fallback, not the source: they're what renders if
// the API is down or the table is empty, so a bad fetch degrades to a slightly
// stale CTA instead of a blank card. Keep them roughly current.
//
// Tracking ids flow into the `target` property on cta_maven / cta_lesson events.
// For a table-driven offer the id is the row's slug, so renaming a slug splits
// that offer's funnel history — rename with care.

import { MONTHS_SHORT } from '../utils/formatDate'

/**
 * What Maven sells, in the three shapes it comes in:
 *
 *  - `course`   — a paid multi-week cohort. Runs as a window (startsAt→endsAt);
 *                 with no endsAt it's evergreen, e.g. an open waitlist.
 *  - `lesson`   — a free Lightning Lesson. A single dated moment, over when it's
 *                 over. This is what gets the homepage flagship slot.
 *  - `workshop` — a paid one-off session. Dated like a lesson, paid like a
 *                 course, so it expires on the day but can be the paid CTA.
 */
export type OfferKind = 'course' | 'lesson' | 'workshop'

const OFFER_KINDS = new Set<OfferKind>(['course', 'lesson', 'workshop'])

/** True for the shapes that are a single dated session rather than a window. */
const isDatedSession = (kind: OfferKind) => kind === 'lesson' || kind === 'workshop'

export interface Offer {
  id: string
  kind: OfferKind
  title: string
  blurb: string
  url: string
  /** Optional promo line (discount code + urgency). */
  promo?: string
  /** When the live session starts, ISO. Absent on evergreen offers. */
  startsAt?: string
  /** How long the live session runs. */
  durationMins?: number
}

/** How each kind is labelled to a reader — an eyebrow above a card. */
export const OFFER_LABEL: Record<OfferKind, string> = {
  course: 'Live cohort on Maven',
  lesson: 'Free Lightning Lesson on Maven',
  workshop: 'Live workshop on Maven',
}

/**
 * The same thing as a noun phrase, to drop mid-sentence ("Go deeper: the live
 * cohort"). Separate from OFFER_LABEL because lowercasing that one also
 * lowercases Maven.
 */
export const OFFER_NOUN: Record<OfferKind, string> = {
  course: 'the live cohort',
  lesson: 'the free session',
  workshop: 'the live workshop',
}

/**
 * Where "book a call" goes. An internal path now, not cal.com: the booking flow
 * lives on the site (/mentorship/book/:slug → /mentorship/booking/:reference),
 * so a reader never leaves for a scheduler that knows nothing about what they
 * were reading. Anything rendering this must treat a leading "/" as a route, not
 * an outbound link.
 */
export const BOOK_CALL_URL = '/mentorship/book/discovery-call'
export const MAVEN_PROFILE_URL = 'https://maven.com/gazar'

/** Sessions are scheduled in London, so that's what readers are shown. */
const LONDON = 'Europe/London'
/** A lesson with no stated duration is assumed to be over an hour after it starts. */
const DEFAULT_LESSON_MINS = 60

export const COURSES = {
  seniorToStaff: {
    id: 'senior-to-staff',
    kind: 'course',
    title: 'From Senior to Staff: Master the Architecture Skills That Get You Promoted',
    blurb:
      'A live cohort on the system-design judgment and influence that gets engineers promoted past senior: how to make the architecture call, and defend it to your team and leadership.',
    url: 'https://maven.com/gazar/senior-to-staff-engineer',
  },
  // Cohort 1 ran Jul 13 – Aug 23, 2026 and is finished. Kept only so the
  // archived scholarship page can name what it was for — never route a live CTA
  // here, and drop it once a Cohort 2 exists to replace it.
  aiAgents: {
    id: 'ai-agents',
    kind: 'course',
    title: 'Production-Ready Systems with LLMs and Agents',
    blurb:
      'A hands-on Maven cohort: six weeks, twelve live sessions, five graded projects where you ship real LLM and agent systems that survive real traffic, cost, and failure, not notebooks.',
    url: 'https://maven.com/gazar/production-ready-systems-with-llms-and-agents-an-intensive-for-engineers',
  },
} as const satisfies Record<string, Offer>

/**
 * Lightning Lessons that have run, kept as a record of what the site once
 * promoted. Nothing routes a live CTA here, and nothing should: a dated session
 * in a source file is stale the day after it airs, and the admin table is the
 * only thing that knows what is actually on. See FALLBACK_OFFERS.
 */
export const LESSONS = {
  seniorVsStaff: {
    id: 'ai-agents-senior-vs-staff',
    kind: 'lesson',
    title: 'System Design for AI Agents: Senior vs Staff',
    url: 'https://maven.com/p/eb3b31/system-design-for-ai-agents-senior-vs-staff',
    blurb:
      'The five things that break every LLM agent after the demo, and the design decisions that stop each one. Free, live, 45 minutes: the production judgment that separates senior engineers from staff when AI is in the stack.',
    startsAt: '2026-07-21T17:30:00.000Z', // 6:30pm London
    durationMins: 45,
  },
} as const satisfies Record<string, Offer>

/** Fallback paid offer: used when the table has no live course. */
const FALLBACK_COURSE: Offer = COURSES.seniorToStaff
// There is deliberately no fallback *lesson*. A cohort has no date on it, so a
// stale one is merely out of date; a lesson is a single dated moment, so a
// stale one is a lie. See FALLBACK_OFFERS.

// ── Reading the table ────────────────────────────────────────────────────────

/** One row of GET /api/content/maven. Everything but the title is optional. */
interface OfferingRow {
  slug?: unknown
  title?: unknown
  kind?: unknown
  blurb?: unknown
  url?: unknown
  startsAt?: unknown
  endsAt?: unknown
  durationMins?: unknown
  promo?: unknown
  order?: unknown
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

const time = (v: unknown): number | undefined => {
  const s = str(v)
  if (!s) return undefined
  const t = new Date(s).getTime()
  return Number.isNaN(t) ? undefined : t
}

/** A row we can actually render, plus the derived fields selection needs. */
interface ParsedOffering {
  offer: Offer
  startsAtMs?: number
  /** The instant this stops being promoted. Absent means evergreen. */
  expiresAtMs?: number
  order: number
}

// The table is admin-edited, so treat every row as untrusted: a row missing a
// title or url can't be rendered as a CTA and is dropped rather than shown half
// empty.
function parseRow(raw: OfferingRow): ParsedOffering | undefined {
  const title = str(raw.title)
  const url = str(raw.url)
  const slug = str(raw.slug)
  if (!title || !url || !slug) return undefined

  const kind = OFFER_KINDS.has(raw.kind as OfferKind) ? (raw.kind as OfferKind) : 'course'
  const startsAtMs = time(raw.startsAt)
  const durationMins = typeof raw.durationMins === 'number' && raw.durationMins > 0 ? raw.durationMins : undefined

  // A lesson or workshop is a moment: it's over once the session has run. A
  // cohort is a window: it runs until endsAt, and an endless one is evergreen
  // (an open waitlist has nothing to expire).
  const expiresAtMs = isDatedSession(kind)
    ? (time(raw.endsAt) ?? (startsAtMs != null ? startsAtMs + (durationMins ?? DEFAULT_LESSON_MINS) * 60_000 : undefined))
    : time(raw.endsAt)

  return {
    offer: {
      id: slug,
      kind,
      title,
      blurb: str(raw.blurb) ?? '',
      url,
      promo: str(raw.promo),
      startsAt: str(raw.startsAt),
      durationMins,
    },
    startsAtMs,
    expiresAtMs,
    order: typeof raw.order === 'number' ? raw.order : 0,
  }
}

export interface LiveOffers {
  /** The next free session, if one is scheduled and hasn't happened yet. */
  lesson?: Offer
  /** The paid offer to point at. Always set, so a CTA is never headless. */
  course: Offer
  /** Everything still on, in admin sort order — for list UIs. */
  all: Offer[]
}

/**
 * Derive what's live from the table.
 *
 * `now` is injected rather than read from the clock so this stays pure and the
 * expiry boundaries are testable.
 */
export function selectLiveOffers(rows: unknown, now: Date = new Date()): LiveOffers {
  const at = now.getTime()
  const live = (Array.isArray(rows) ? rows : [])
    .map((r) => parseRow((r ?? {}) as OfferingRow))
    .filter((p): p is ParsedOffering => p != null)
    // Not finished yet…
    .filter((p) => p.expiresAtMs == null || p.expiresAtMs > at)
    // …and, for a dated session, actually scheduled. An undated lesson is a
    // past one nobody has archived, or a draft — either way we can't tell a
    // reader when to turn up, so it isn't promotable.
    .filter((p) => !isDatedSession(p.offer.kind) || p.startsAtMs != null)

  // Soonest first: the next session is the one a reader can still turn up to.
  const lesson = live
    .filter((p) => p.offer.kind === 'lesson')
    .sort((a, b) => a.startsAtMs! - b.startsAtMs!)[0]

  // A cohort is the natural "go deeper" step; a paid workshop stands in when no
  // cohort is running, so the paid CTA is never empty while something is sellable.
  const paid = live
    .filter((p) => p.offer.kind === 'course' || p.offer.kind === 'workshop')
    .sort((a, b) => (a.offer.kind === b.offer.kind ? a.order - b.order : a.offer.kind === 'course' ? -1 : 1))[0]

  const all = [...live].sort((a, b) => a.order - b.order || (a.startsAtMs ?? 0) - (b.startsAtMs ?? 0)).map((p) => p.offer)

  return { lesson: lesson?.offer, course: paid?.offer ?? FALLBACK_COURSE, all }
}

/**
 * What to render when the table can't be read at all (API down, network error).
 *
 * No `lesson`, on purpose. This used to carry a hardcoded session, and because
 * the constant is only as fresh as the last person to edit it, on 29 Aug 2026 a
 * single failed fetch would have advertised a session that ran on 21 Jul as the
 * next one, on the homepage. A reader has no way to tell a fallback from a
 * fact, so the honest degraded state is to say nothing about dates rather than
 * to guess at one.
 *
 * Deriving a date forward would be worse, not better: it would invent a session
 * that does not exist, and a value computed from the clock at module load
 * differs between the server process and the browser, which is the hydration
 * mismatch the formatters above exist to avoid.
 *
 * Every caller already handles a missing lesson, because `selectLiveOffers`
 * returns none whenever nothing is scheduled, which is the common case between
 * sessions. So this path renders exactly what a quiet week renders: the paid
 * cohort, and no invitation to a session that is not happening.
 */
export const FALLBACK_OFFERS: LiveOffers = { course: FALLBACK_COURSE, all: [] }

/**
 * Sessions that have already run, newest first.
 *
 * The mirror image of the live selection: everything selectLiveOffers drops for
 * being over is exactly what belongs here. Maven keeps a free replay at the same
 * URL once a lesson has aired, so a finished session is still worth something —
 * it just stops being an invitation and becomes a recording.
 *
 * Undated lessons are left out, same as the live path: with no date there's
 * nothing to order them by and no way to tell a reader when they aired.
 */
export function selectPastLessons(rows: unknown, now: Date = new Date()): Offer[] {
  const at = now.getTime()
  return (Array.isArray(rows) ? rows : [])
    .map((r) => parseRow((r ?? {}) as OfferingRow))
    .filter((p): p is ParsedOffering => p != null)
    .filter((p) => p.offer.kind === 'lesson' && p.startsAtMs != null)
    .filter((p) => p.expiresAtMs != null && p.expiresAtMs <= at)
    .sort((a, b) => b.startsAtMs! - a.startsAtMs!)
    .map((p) => p.offer)
}

// ── Formatting ───────────────────────────────────────────────────────────────
//
// Every string below is server-rendered and then rendered again by the
// visitor's browser during hydration, so it has to come out byte for byte
// identical in two different JavaScript engines. Intl's *numbers* do. Intl's
// *names* do not.
//
// The one that bit us: CLDR 42 changed the en-GB abbreviation for September
// from "Sep" to "Sept". Node and Blink shipped that change and WebKit has not,
// so the homepage server-rendered "Tue 1 Sept" and every Safari, iOS Chrome and
// in-app WKWebView hydrated "Tue 1 Sep". That is React #418 (GlitchTip issue
// 148, all eight events WebKit), and it threw the server-rendered homepage away
// and rebuilt it on the client. The sibling bug on the resume was a timezone
// (see utils/tenure.ts) and this one is locale data, but it is the same rule:
// SSR text must not be derived from anything the two engines may disagree on.
//
// So the month and weekday names are ours, and Intl is used only for the thing
// it is genuinely needed for, working out which London day and hour an instant
// falls on. BST is not a fixed offset, and those fields are numeric everywhere.

// One list, not two: utils/formatDate.ts owns it and the article dates use it
// through formatUtcShortDate, so a correction lands in both places at once.
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

interface LondonParts {
  year: number
  /** 1-12. */
  month: number
  day: number
  /** 0-23. */
  hour: number
  minute: number
  /** 0-6, Sunday first, to index WEEKDAYS_SHORT. */
  weekday: number
}

const LONDON_PARTS_FMT = new Intl.DateTimeFormat('en-GB', {
  timeZone: LONDON,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

/** The London wall-clock fields of an ISO instant, or null if it isn't one. */
function londonParts(startsAt: string | undefined): LondonParts | null {
  if (!startsAt) return null
  const d = new Date(startsAt)
  if (Number.isNaN(d.getTime())) return null
  const parts = LONDON_PARTS_FMT.formatToParts(d)
  const n = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value ?? NaN)
  const year = n('year')
  const month = n('month')
  const day = n('day')
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null
  return {
    year,
    month,
    day,
    // h23 is 0-23, but engines have shipped "24" for midnight before now.
    hour: n('hour') % 24,
    minute: n('minute'),
    // Arithmetic, not locale data: the names are ours, so all that is needed is
    // the index, and Date.UTC on the civil date gives it without Intl at all.
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
  }
}

/** e.g. "6:30pm": 12-hour, lowercase, no space, which is the house form. */
function clock12({ hour, minute }: LondonParts): string {
  const h = hour % 12 === 0 ? 12 : hour % 12
  return `${h}:${String(minute).padStart(2, '0')}${hour < 12 ? 'am' : 'pm'}`
}

/** e.g. "29 Apr 2026" — for a session that has already aired. */
export function formatPastDate(startsAt: string | undefined): string {
  const p = londonParts(startsAt)
  if (!p) return ''
  return `${p.day} ${MONTHS_SHORT[p.month - 1]} ${p.year}`
}

/** e.g. "Tue, 21 Jul · 6:30pm London". Empty string if there's no usable date. */
export function formatSessionDate(startsAt: string | undefined): string {
  const p = londonParts(startsAt)
  if (!p) return ''
  // The comma is ours: en-GB renders the combined weekday form as "Tue 21 Jul",
  // and that punctuation drifts between locales and ICU versions too.
  return `${WEEKDAYS_SHORT[p.weekday]}, ${p.day} ${MONTHS_SHORT[p.month - 1]} · ${clock12(p)} London`
}

/**
 * e.g. "Tue 18 Aug" — the day only, for a dense list of what's coming up.
 *
 * The schedule rail repeats this once per row, where the time and timezone are
 * the same for every session and so carry no information. The full form stays
 * on the flagship card, which is the one a reader needs in order to turn up.
 */
export function formatSessionDayShort(startsAt: string | undefined): string {
  const p = londonParts(startsAt)
  if (!p) return ''
  return `${WEEKDAYS_SHORT[p.weekday]} ${p.day} ${MONTHS_SHORT[p.month - 1]}`
}

/** e.g. "45 minutes". Empty string if unknown. */
export function formatDuration(durationMins: number | undefined): string {
  return durationMins ? `${durationMins} minutes` : ''
}

// ── Routing rules (code, not content) ────────────────────────────────────────

// ── The 1:1 offer ────────────────────────────────────────────────────────────
//
// This used to be a boolean gated on two categories, so 384 of 503 articles
// never mentioned that Gaz takes clients at all — including every AI post
// feeding the AI funnel. The reach was the bug.
//
// But "show it everywhere" is the lazy fix: a reader deep in a sorting-algorithm
// post is not weighing a staff promotion, they're probably prepping for an
// interview — and there's a service for exactly that. So the question isn't
// whether to show the offer, it's which one. Every angle below maps to a real
// service on /mentorship; none of this invents a product.
export type MentorshipAngle = 'career' | 'interview' | 'ai-review'

export interface MentorshipOffer {
  /** Stable tracking key — flows into cta_mentorship's `target`. */
  id: MentorshipAngle
  /** Small label above the pitch. */
  eyebrow: string
  /** The pitch itself, one line. */
  title: string
  /** Supporting line. Rail only; the article footer is tighter. */
  blurb: string
}

export const MENTORSHIP: Record<MentorshipAngle, MentorshipOffer> = {
  career: {
    id: 'career',
    eyebrow: '1:1',
    title: 'Mentorship for engineers targeting staff and principal',
    blurb: 'Career strategy and system-design judgment, one-to-one, with someone who has made the calls.',
  },
  interview: {
    id: 'interview',
    eyebrow: '1:1',
    title: 'Interview prep with someone who runs the loop',
    blurb: 'Mock interviews, system-design practice, and honest feedback on where you actually lose points.',
  },
  'ai-review': {
    id: 'ai-review',
    eyebrow: '1:1',
    title: 'Get your LLM or agent system reviewed',
    blurb: 'Bring the architecture. I stress-test it like a staff engineer and send written feedback.',
  },
}

// Category → which angle fits the person reading. Anything unlisted gets the
// career angle: the site's whole premise is the senior→staff move, so that's
// the honest default rather than silence.
const ANGLE_BY_CATEGORY: Record<string, MentorshipAngle> = {
  leadership: 'career',
  'system-design': 'career',
  'clean-code': 'career',
  'design-pattern': 'career',
  reviews: 'career',
  'artificial-intelligence': 'ai-review',
  algorithm: 'interview',
  'data-structure': 'interview',
  typescript: 'interview',
  sql: 'interview',
  database: 'interview',
}

export function mentorshipFor(category: string | undefined): MentorshipOffer {
  return MENTORSHIP[(category && ANGLE_BY_CATEGORY[category]) || 'career']
}

export interface ContextualOffer {
  course: Offer
  lesson?: Offer
  /** The 1:1 pitch that fits this reader. Always present — see above. */
  mentorship: MentorshipOffer
}

export function getContextualCTA(category: string | undefined, offers: LiveOffers = FALLBACK_OFFERS): ContextualOffer {
  return {
    course: offers.course,
    lesson: offers.lesson,
    mentorship: mentorshipFor(category),
  }
}
